// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { apiClient } from './client';

export interface AccessibleCompany {
  readonly id: string;
  readonly tenantId: string;
  readonly code: string;
  readonly displayName: string;
  readonly legalName: string;
  readonly vkn: string | null;
  readonly countryCode: string;
  readonly currency: string;
  readonly chartOfAccounts: string;
  readonly fiscalYearStartMonth: number;
  readonly isDefault: boolean;
  readonly role: string;
  readonly logoUrl?: string | null;
  /** Belge çıktısındaki "HAZIRLAYAN" bloğu — teklif/fatura/irsaliyede basılır. */
  readonly taxOffice?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly website?: string | null;
  readonly address?: string | null;
}

export interface Branch {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly isHeadquarters: boolean;
  readonly role: string;
}

export interface FiscalPeriod {
  readonly id: string;
  readonly year: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly status: 'open' | 'closing' | 'closed';
  readonly closedAt: string | null;
}

export interface Warehouse {
  readonly id: string;
  readonly branchId: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly isDefault: boolean;
}

export interface CreateCompanyRequest {
  readonly code?: string;
  readonly legalName: string;
  readonly displayName?: string;
  readonly vkn?: string;
  readonly countryCode?: string;
  readonly currency?: string;
  readonly chartOfAccounts?: string;
  readonly fiscalYearStartMonth?: number;
  readonly timezone?: string;
  readonly taxOffice?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly website?: string;
  readonly address?: string;
}

export interface CreateBranchRequest {
  readonly code?: string;
  readonly name: string;
  readonly type?: string;
  readonly phone?: string;
}

export interface CreateFiscalPeriodRequest {
  readonly year: number;
  readonly startDate?: string;
  readonly endDate?: string;
}

// Şirket listesi başka bir ekrandan değiştiğinde (entegratör kimliği kaydedilip şirketler çekildiğinde)
// üst çubuktaki seçici kendini yeniler; kullanıcıya "seçiciyi yenileyin" denmez.
export const COMPANIES_CHANGED_EVENT = 'elpo:companies-changed';

export function notifyCompaniesChanged(): void {
  window.dispatchEvent(new Event(COMPANIES_CHANGED_EVENT));
}

export const companyService = {
  async accessible(): Promise<AccessibleCompany[]> {
    const { data } = await apiClient.get<AccessibleCompany[]>('/api/companies/accessible');
    return data;
  },

  async create(req: CreateCompanyRequest): Promise<{ readonly id: string; readonly code: string }> {
    const { data } = await apiClient.post<{ id: string; code: string }>('/api/companies', req);
    return data;
  },

  async branches(companyId: string): Promise<Branch[]> {
    const { data } = await apiClient.get<Branch[]>(`/api/companies/${companyId}/branches`);
    return data;
  },

  async createBranch(companyId: string, req: CreateBranchRequest): Promise<{ readonly id: string }> {
    const { data } = await apiClient.post<{ id: string }>(`/api/companies/${companyId}/branches`, req);
    return data;
  },

  async fiscalPeriods(companyId: string): Promise<FiscalPeriod[]> {
    const { data } = await apiClient.get<FiscalPeriod[]>(`/api/companies/${companyId}/fiscal-periods`);
    return data;
  },

  async createFiscalPeriod(companyId: string, req: CreateFiscalPeriodRequest): Promise<{ readonly id: string }> {
    const { data } = await apiClient.post<{ id: string }>(`/api/companies/${companyId}/fiscal-periods`, req);
    return data;
  },

  async warehouses(companyId: string): Promise<Warehouse[]> {
    const { data } = await apiClient.get<Warehouse[]>(`/api/companies/${companyId}/warehouses`);
    return data;
  },

  async updateCompany(companyId: string, req: Partial<CreateCompanyRequest>): Promise<void> {
    await apiClient.put(`/api/companies/${companyId}`, req);
  },

  async deleteCompany(companyId: string): Promise<void> {
    await apiClient.delete(`/api/companies/${companyId}`);
  },

  // Şirket logosu (belge/makbuz çıktılarında basılır). data:image/… veya https; boş → kaldır.
  async setLogo(companyId: string, logoUrl: string): Promise<void> {
    await apiClient.put(`/api/company-group/${companyId}/logo`, { logoUrl });
  },

  async updateBranch(companyId: string, branchId: string, req: Partial<CreateBranchRequest>): Promise<void> {
    await apiClient.put(`/api/companies/${companyId}/branches/${branchId}`, req);
  },

  async deleteBranch(companyId: string, branchId: string): Promise<void> {
    await apiClient.delete(`/api/companies/${companyId}/branches/${branchId}`);
  },

  async closeFiscalPeriod(companyId: string, periodId: string): Promise<void> {
    await apiClient.post(`/api/companies/${companyId}/fiscal-periods/${periodId}/close`);
  },

  async reopenFiscalPeriod(companyId: string, periodId: string): Promise<void> {
    await apiClient.post(`/api/companies/${companyId}/fiscal-periods/${periodId}/reopen`);
  },
};
